import React, { useEffect } from 'react';
import { View } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { track } from '@/lib/analytics';
import { useAuth } from '@/lib/supabase';
import { mode } from '@/lib/config/app-mode';

export default function OnboardingLayout() {
  const { user, isLoading } = useAuth();
  const router = useRouter();

  // Onboarding requires auth — direct deep-link access without a session is blocked.
  // Mock mode bypass is honored to keep demo flows working.
  const hasAuth = !!user || mode.isMock;

  useEffect(() => {
    if (!isLoading && !hasAuth) {
      router.replace('/login');
    }
  }, [isLoading, hasAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !hasAuth) return <View style={{ flex: 1 }} />;

  return (
    <Stack
      screenListeners={{
        focus: (e) => {
          track('onboarding_step_completed', { step: e.target?.split('-')[0] ?? 'unknown' });
        },
      }}
      screenOptions={{
        headerShown: false,
        gestureEnabled: false,
        animation: 'slide_from_right',
      }}
    >
      {/* Startup onboarding flow — canonical step list + order: src/lib/onboarding/steps.ts.
          Founder entry is `personal` (the legacy `welcome` bookend was removed in Task 3). */}
      <Stack.Screen name="startup-essentials" />
      <Stack.Screen name="data-input-choice" />
      <Stack.Screen name="complete" />

      {/* Investor onboarding flow (2 steps): Identity → Thesis → Complete (redesign Task 6).
          LinkedIn verification is an optional sub-screen launched from identity. */}
      <Stack.Screen name="investor-identity" />
      <Stack.Screen name="investor-thesis" />
      <Stack.Screen name="linkedin-verify" />

      {/* Investor "Enhance profile" screens — reachable post-onboarding only,
          never routed into by the onboarding flow. */}
      <Stack.Screen name="investor-portfolio" />
      <Stack.Screen name="investment-history" />
      <Stack.Screen name="portfolio-companies" />

      {/* Startup sub-paths (accessible from data-input-choice, not in main step sequence) */}
      <Stack.Screen name="pitch-deck-upload" />
      <Stack.Screen name="pitch-deck-review" />
      <Stack.Screen name="data-room-connect" />

      {/* Legacy screens (kept as routes for post-onboarding "Enhance Profile") */}
      <Stack.Screen name="startup-company" />
      <Stack.Screen name="startup-personal" />
      <Stack.Screen name="questionnaire-problem" />
      <Stack.Screen name="questionnaire-traction" />
      <Stack.Screen name="questionnaire-market" />
      <Stack.Screen name="fundraising-status" />
      <Stack.Screen name="serial-founder" />
    </Stack>
  );
}
